import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Button } from '../../../../components/ui/Button';
import { Input, FormField } from '../../../../components/ui/FormField';
import { Money } from '../../../../components/ui/Money';
import { useToast } from '../../../../components/ui/Toast';
import { GeneralTransactionLineFields } from '../../../../features/accounting/components/GeneralTransactionLineFields';
import {
  generalTransactionsHooks,
  type GeneralTransactionLineInput,
} from '../../../../features/accounting/network/general-transactions.api';

export const Route = createFileRoute('/_auth/accounting/general-transactions/new')({
  component: NewGeneralTransactionPage,
});

const emptyLine = (direction: 'DEBIT' | 'CREDIT' = 'DEBIT'): GeneralTransactionLineInput => ({
  accountId: '',
  amountPaise: 0,
  direction,
  narration: '',
});

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function NewGeneralTransactionPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = generalTransactionsHooks.useCreate();

  const [type, setType] = React.useState<'CASH' | 'JOURNAL'>('JOURNAL');
  const [entryDate, setEntryDate] = React.useState(todayISO());
  const [particulars, setParticulars] = React.useState('');
  const [journalLines, setJournalLines] = React.useState<GeneralTransactionLineInput[]>([
    emptyLine('DEBIT'),
    emptyLine('CREDIT'),
  ]);
  const [cashLine, setCashLine] = React.useState<GeneralTransactionLineInput>(emptyLine('CREDIT'));
  const [cashAmount, setCashAmount] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const updateJournalLine = (index: number, next: GeneralTransactionLineInput) => {
    setJournalLines((lines) => lines.map((l, i) => (i === index ? next : l)));
  };

  const totalDebit = journalLines.reduce(
    (sum, l) => (l.direction === 'DEBIT' ? sum.add(l.amountPaise || 0) : sum),
    new Decimal(0),
  );
  const totalCredit = journalLines.reduce(
    (sum, l) => (l.direction === 'CREDIT' ? sum.add(l.amountPaise || 0) : sum),
    new Decimal(0),
  );
  const journalBalanced = totalDebit.equals(totalCredit) && totalDebit.greaterThan(0);
  const isUnbalanced = (totalDebit.greaterThan(0) || totalCredit.greaterThan(0)) && !totalDebit.equals(totalCredit);

  const cashAmountPaise = new Decimal(cashAmount || 0).mul(100).round().toNumber();

  const canSubmit =
    !!particulars.trim() &&
    !!entryDate &&
    (type === 'JOURNAL'
      ? journalBalanced && journalLines.every((l) => l.accountId)
      : !!cashLine.accountId && cashAmountPaise > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const idempotencyKey = crypto.randomUUID();
      const lines: GeneralTransactionLineInput[] =
        type === 'JOURNAL' ? journalLines : [{ ...cashLine, amountPaise: cashAmountPaise }];

      await createMutation.mutateAsync({
        type,
        entryDate,
        particulars: particulars.trim(),
        lines,
        idempotencyKey,
      });

      toast({ type: 'success', title: 'Submitted for approval', description: 'The transaction will post once approved by a checker.' });
      navigate({ to: '/accounting/general-transactions' });
    } catch (err: unknown) {
      const error = err as { message?: string };
      console.error('Failed to submit general transaction', err);
      toast({ type: 'error', title: 'Error', description: error.message || 'Failed to submit transaction.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormPageLayout
      title="New Transaction"
      backTo="/accounting/general-transactions"
      variant="plain"
    >
      <div className="space-y-6">
        <div className="flex gap-2">
          <Button
            type="button"
            variant={type === 'JOURNAL' ? 'primary' : 'secondary'}
            onClick={() => setType('JOURNAL')}
            disabled={isSubmitting}
          >
            Journal Entry
          </Button>
          <Button
            type="button"
            variant={type === 'CASH' ? 'primary' : 'secondary'}
            onClick={() => setType('CASH')}
            disabled={isSubmitting}
          >
            Cash Transaction
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Date" required>
            <Input
              type="date"
              className="w-full"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              disabled={isSubmitting}
            />
          </FormField>
        </div>

        <FormField label="Particulars" required>
          <textarea
            className="w-full border border-surface-3 rounded p-2 min-h-[80px]"
            value={particulars}
            maxLength={250}
            onChange={(e) => setParticulars(e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>

        {type === 'CASH' ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={cashLine.direction === 'CREDIT' ? 'primary' : 'secondary'}
                onClick={() => setCashLine((l) => ({ ...l, direction: 'CREDIT' }))}
                disabled={isSubmitting}
              >
                Cash In (Receipt)
              </Button>
              <Button
                type="button"
                variant={cashLine.direction === 'DEBIT' ? 'primary' : 'secondary'}
                onClick={() => setCashLine((l) => ({ ...l, direction: 'DEBIT' }))}
                disabled={isSubmitting}
              >
                Cash Out (Payment)
              </Button>
            </div>

            <GeneralTransactionLineFields line={cashLine} onChange={setCashLine} disabled={isSubmitting} />

            <FormField label="Amount" required>
              <Input
                type="number"
                min="0"
                step="0.01"
                className="w-48"
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                disabled={isSubmitting}
              />
            </FormField>
          </div>
        ) : (
          <div className="space-y-4">
            {journalLines.map((line, index) => (
              <div key={index} className="bg-surface-0 border border-surface-2 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-3 gap-4 items-end">
                  <div className="col-span-2">
                    <GeneralTransactionLineFields
                      line={line}
                      onChange={(next) => updateJournalLine(index, next)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    <FormField label="Direction">
                      <select
                        className="border border-surface-3 rounded px-2 py-2 text-sm bg-surface-0"
                        value={line.direction}
                        onChange={(e) => updateJournalLine(index, { ...line, direction: e.target.value as 'DEBIT' | 'CREDIT' })}
                        disabled={isSubmitting}
                      >
                        <option value="DEBIT">Debit</option>
                        <option value="CREDIT">Credit</option>
                      </select>
                    </FormField>
                    <FormField label="Amount">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-28"
                        value={line.amountPaise ? new Decimal(line.amountPaise).div(100).toNumber() : ''}
                        onChange={(e) =>
                          updateJournalLine(index, {
                            ...line,
                            amountPaise: new Decimal(e.target.value || 0).mul(100).round().toNumber(),
                          })
                        }
                        disabled={isSubmitting}
                      />
                    </FormField>
                    {journalLines.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-danger-500"
                        onClick={() => setJournalLines((lines) => lines.filter((_, i) => i !== index))}
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-accent-600 font-medium"
              onClick={() => setJournalLines((lines) => [...lines, emptyLine()])}
            >
              + Add Line
            </Button>

            <div className="bg-surface-50 border-t-2 border-surface-3 p-3 flex justify-end gap-8 text-sm font-medium">
              <span>Total Debit: <Money paise={totalDebit.toNumber()} /></span>
              <span>Total Credit: <Money paise={totalCredit.toNumber()} /></span>
            </div>
            {isUnbalanced && (
              <div className="py-2 px-4 text-danger-600 text-sm text-center bg-danger-50 rounded">
                Journal entry is unbalanced. Difference: <Money paise={totalDebit.minus(totalCredit).abs().toNumber()} />
              </div>
            )}
          </div>
        )}

        <div className="flex gap-4 justify-end">
          <Button variant="secondary" onClick={() => navigate({ to: '/accounting/general-transactions' })} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit || isSubmitting} loading={isSubmitting}>
            Submit for Approval
          </Button>
        </div>
      </div>
    </FormPageLayout>
  );
}
